import { useMemo } from "react";
import { MRT_ColumnDef } from "mantine-react-table";
import { Badge, Tooltip, ActionIcon, Text, Button, Group } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import type { RegtankHistoryData } from "../Regtank.types";
import { RegtankOnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { useStyles } from "../Regtank.styles";

interface UseVerificationOnboardingColumnsProps {
  getNewSubmittedOn: (requestId?: string, sessionId?: string) => boolean;
  getNewReviewedOn: (
    reviewedOn: string,
    requestId?: string,
    sessionId?: string
  ) => string | undefined;
  handleViewDocumentFront: (
    requestId: string,
    documentIdSubmittedDate: string
  ) => boolean;
  handleViewDocumentBack: (
    requestId: string,
    documentIdSubmittedDate: string
  ) => boolean;
  handleViewLiveness: (
    requestIdOrSessionId: string,
    submittedDate: string
  ) => boolean;
  handleClick: (requestIdOrSessionId: string, type: string) => void;
  getRegtankHistoryVerificationResult: (
    requestId: string,
    status: RegtankOnboardingStatusEnum
  ) => string | undefined;
  getRegtankVerificationResultColor: (
    requestId: string,
    status: RegtankOnboardingStatusEnum
  ) => string | undefined;
  getNewFailureReason: (
    reason: string,
    requestId?: string,
    sessionId?: string
  ) => string[];
  getNewRemark: (
    remark: string,
    requestId?: string,
    sessionId?: string
  ) => string | undefined;
  getNewReattemptApproval: (
    reattemptApproval: string,
    requestId?: string,
    sessionId?: string
  ) => string | undefined;
  getReattemptApprovalColor: (reattemptApproval: string) => string | undefined;
}

/**
 * Note: Helper functions are passed as props from parent component.
 * The parent wraps utility functions from utils/regtankHelpers.ts with useCallback
 * to ensure stable references and prevent unnecessary re-renders.
 */

/**
 * Column definitions for verification onboarding history table
 * Used for displaying detailed onboarding verification history with documents and liveness checks
 */
export const useVerificationOnboardingColumns = ({
  getNewSubmittedOn,
  getNewReviewedOn,
  handleViewDocumentFront,
  handleViewDocumentBack,
  handleViewLiveness,
  handleClick,
  getRegtankHistoryVerificationResult,
  getRegtankVerificationResultColor,
  getNewFailureReason,
  getNewRemark,
  getNewReattemptApproval,
  getReattemptApprovalColor,
}: UseVerificationOnboardingColumnsProps) => {
  const { classes } = useStyles();
  return useMemo<MRT_ColumnDef<RegtankHistoryData>[]>(
    () => [
      {
        accessorKey: "linkSentOn",
        header: "Link Generated On",
      },
      {
        accessorKey: "submittedOn",
        header: "Verification Submitted On",
        Cell: ({ row }) => {
          const label = getNewSubmittedOn(row.original.requestId, "");
          if (!label) return null;
          return <Text>{row.original.submittedOn}</Text>;
        },
      },
      {
        accessorKey: "reviewedOn",
        header: "Reviewed On",
        Header: ({ column }) => (
          <>
            <span>{column.columnDef.header}</span>
          </>
        ),
        Cell: ({ row }) => {
          const label = getNewReviewedOn(
            row.original.reviewedOn || "",
            row.original.requestId
          );
          return <Text>{label}</Text>;
        },
      },
      {
        id: "documentId",
        header: "ID Document",
        Cell: ({ row }) => (
          <Group>
            <Button
              disabled={handleViewDocumentFront(
                row.original.requestId,
                row.original.documentIdSubmittedDate
              )}
              onClick={() =>
                handleClick(row.original.requestId, "documentFrontPhotoUrl")
              }
            >
              VIEW FRONT PHOTO
            </Button>
            <Button
              disabled={handleViewDocumentBack(
                row.original.requestId,
                row.original.documentIdSubmittedDate
              )}
              onClick={() =>
                handleClick(row.original.requestId, "documentBackPhotoUrl")
              }
            >
              VIEW BACK PHOTO
            </Button>
          </Group>
        ),
      },
      {
        id: "livenessCheck",
        header: "Liveness Check",
        Cell: ({ row }) => (
          <Group>
            <Button
              disabled={handleViewLiveness(
                row.original.requestId,
                row.original.submittedOn
              )}
              onClick={() =>
                handleClick(row.original.requestId, "livenessSelfieUrl")
              }
            >
              VIEW PHOTO
            </Button>
            <Button
              disabled={handleViewLiveness(
                row.original.requestId,
                row.original.submittedOn
              )}
              onClick={() =>
                handleClick(row.original.requestId, "livenessVideoUrl")
              }
            >
              VIEW VIDEO
            </Button>
          </Group>
        ),
      },
      {
        accessorKey: "verificationResult",
        header: "Verification Result",
        enableSorting: false,
        Cell: ({ row }) => {
          const label = getRegtankHistoryVerificationResult(
            row.original.requestId,
            row.original.verificationResult
          );
          if (!label) return null;
          return (
            <Badge
              color={getRegtankVerificationResultColor(
                row.original.requestId,
                row?.original.verificationResult
              )}
            >
              {getRegtankHistoryVerificationResult(
                row.original.requestId,
                row.original.verificationResult
              )}
            </Badge>
          );
        },
      },
      {
        accessorKey: "reason",
        header: "Failure Reason",
        Cell: ({ row }) => {
          const label = getNewFailureReason(
            row.original.reason || "",
            row.original.requestId,
            ""
          );
          if (!Array.isArray(label) || label.length === 0) return null;
          if (label[0].startsWith("-") || label.length < 2) {
            return <Text>{label}</Text>;
          }
          return (
            <ul className={classes.reasonList}>
              {label.map((x, index) => (
                <li className={classes.reasonItem} key={index}>
                  {x}
                </li>
              ))}
            </ul>
          );
        },
        size: 400,
        enableSorting: false,
      },
      {
        accessorKey: "remark",
        header: "Remark",
        enableSorting: false,
        Cell: ({ row }) => {
          const label = getNewRemark(
            row.original.remark || "",
            row.original.requestId
          );
          return <Text>{label}</Text>;
        },
      },
      {
        accessorKey: "reattemptApproval",
        header: "Reattempt Approval",
        enableSorting: false,
        Cell: ({ row }) => {
          const label = getNewReattemptApproval(
            row.original.reattemptApproval || "",
            row.original.requestId
          );
          if (label === "") return null;
          else if (label === "-") return label;
          return (
            <Badge
              color={getReattemptApprovalColor(
                row.original.reattemptApproval || ""
              )}
            >
              {label}
            </Badge>
          );
        },
      },
    ],
    [
      getNewFailureReason,
      getNewReattemptApproval,
      getNewRemark,
      getNewReviewedOn,
      getNewSubmittedOn,
      getRegtankHistoryVerificationResult,
      getRegtankVerificationResultColor,
      getReattemptApprovalColor,
      handleClick,
      handleViewDocumentBack,
      handleViewDocumentFront,
      handleViewLiveness,
      classes,
    ]
  );
};
